import React, { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import GenerativeIndicatorIcon from '@atlaskit/icon-lab/core/generative-indicator';
import { useUIStore } from '@atlassian/conversation-assistant-store';
import { type ConversationAction, HandleActionType } from '@atlassian/rovo-conversation-actions';
import { useToggleConversationAssistant } from '@confluence/conversation-assistant/entry-points/useToggleConversationAssistant.ts';

const messages = defineMessages({
	insightsNudgeTitle: {
		id: 'page-rovo-config-common.digest-nudge-title.v2',
		defaultMessage: 'Explore your Rovo digest',
		description: 'Title for the Rovo Digest weekly nudge that appears next to the Rovo button',
	},
	insightsNudgeIconLabel: {
		id: 'page-rovo-config-common.digest-nudge-icon-label',
		defaultMessage: 'Digest icon',
		description: 'Accessible label for the icon in the Rovo Digest weekly nudge',
	},
	insightsNudgeSecondaryTitle: {
		id: 'page-rovo-config-common.digest-nudge-secondary-title',
		defaultMessage: "See this week's highlights at a glance",
		description: 'Subtitle shown below the Digest nudge title on hover',
	},
});

/**
 * Hook that creates and returns the Insights weekly nudge action.
 */
export function useInsightsNudgeAction(): ConversationAction {
	const { setActiveMenu } = useUIStore();
	const { formatMessage } = useIntl();
	const { mode, toggleConversationAssistant } = useToggleConversationAssistant();

	const onClick = useCallback(() => {
		if (!mode) {
			toggleConversationAssistant();
		}
		setActiveMenu('pulse');
	}, [mode, toggleConversationAssistant, setActiveMenu]);

	return useMemo(
		() => ({
			id: 'rovo-insights-nudge',
			title: formatMessage(messages.insightsNudgeTitle),
			secondaryTitle: formatMessage(messages.insightsNudgeSecondaryTitle),
			icon: () => (
				<GenerativeIndicatorIcon
					size="small"
					label={formatMessage(messages.insightsNudgeIconLabel)}
				/>
			),
			onClick,
			type: 'ai-action',
			handleType: HandleActionType.Base,
			nudgeContext: {
				id: 'rovo-insights',
			},
		}),
		[onClick, formatMessage],
	);
}
